// The flags this app knows about, and the variant values they carry.
//
// Amplitude Experiment flags are string-valued, not booleans, so the value read
// back is compared against known variant values rather than coerced to a
// boolean. Keeping the keys here (instead of inline string literals at the call
// site) means a renamed flag is a one-line change and the name in the code
// always matches the one configured in Amplitude.

/** Flag keys, exactly as configured on the Amplitude deployment. */
export const FLAGS = {
  // Gates the English/Español switch on the video catalog. English is the
  // default language, so "no-show" leaves the app in its pre-Spanish state.
  VIDEOS_LANGUAGE: "video-platform-videos-language",
};

/** Variant values used by the flags above. */
export const FLAG_VALUES = {
  SHOW: "show",
  NO_SHOW: "no-show",
};

// The variant values that mean "the feature is on".
//
// Two configurations produce the same intent in Amplitude, and the UI must not
// depend on which one was chosen:
//
//   show / no-show  explicit variants, one per state (what we asked for)
//   on              a single variant plus the rollout -- a user inside the
//                   rollout gets "on", everyone else gets Amplitude's built-in
//                   `off` variant, which carries no value at all
//
// Either way the decision is per user: the rollout is bucketed on the user id
// we pass (the learner's email or phone), so "who sees it" is the same
// question in both shapes. Only the name of the winning variant differs.
//
// Deliberately an allow-list, not a deny-list of "no-show"/"off": an
// unrecognised value -- a typo, a renamed variant, a half-configured flag --
// has to read as OFF. A dark feature appearing because nobody recognised the
// string is the failure that matters here.
const ENABLED_VALUES = new Set([FLAG_VALUES.SHOW, "on"]);

/** Whether a variant value means the feature should be shown. */
export const isEnabledValue = (value) => ENABLED_VALUES.has(value);

// What a flag evaluates to when Amplitude is unreachable, unconfigured, or
// still loading. Off, deliberately: a demo-only feature must never appear
// because a network call failed.
export const DEFAULT_FLAG_VALUE = FLAG_VALUES.NO_SHOW;
